import type { ReactNode } from 'react';
import { type LucideIcon } from 'lucide-react';

import { JourneyCanvas } from '../../components/layout/journey-canvas';
import { Card, CardContent } from '../../components/ui/card';
import { BrandMark } from './auth-brand-mark';

interface AuthHighlight {
  icon: LucideIcon;
  title: string;
  description: string;
}

interface AuthMetric {
  value: string;
  label: string;
}

interface AuthShellProps {
  heroBadge: string;
  heroTitle: string;
  heroDescription: string;
  heroHighlights: readonly AuthHighlight[];
  heroMetrics: readonly AuthMetric[];
  formBadge: string;
  formTitle: string;
  formDescription: string;
  children: ReactNode;
  footer?: ReactNode;
}

export function AuthShell({
  heroBadge,
  heroTitle,
  heroDescription,
  heroHighlights,
  heroMetrics,
  formBadge,
  formTitle,
  formDescription,
  children,
  footer,
}: AuthShellProps) {
  return (
    <JourneyCanvas>
      <div className="flex min-h-[calc(100vh-2rem)] flex-col">
        <header className="flex items-center justify-between py-2 sm:py-4">
          <BrandMark logoClassName="h-20 sm:h-24 lg:h-28" />

          <span className="hidden rounded-full border border-white/60 bg-white/65 px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-[#5b615d] shadow-[0_12px_28px_rgba(52,38,26,0.08)] backdrop-blur sm:inline-flex">
            Feito para gestores que vivem de marcações
          </span>
        </header>

        <section className="grid flex-1 items-stretch gap-6 pb-6 pt-2 lg:grid-cols-[minmax(0,1.08fr)_minmax(24rem,0.92fr)]">
          <div className="relative overflow-hidden rounded-[2.4rem] border border-white/15 bg-[linear-gradient(155deg,#18323b_0%,#0f4f4b_55%,#0f766e_100%)] px-6 py-8 text-white shadow-[0_38px_80px_rgba(22,50,59,0.28)] animate-rise sm:px-8 sm:py-10">
            <div className="pointer-events-none absolute right-0 top-0 h-44 w-44 rounded-full bg-white/10 blur-3xl" />
            <div className="pointer-events-none absolute bottom-0 left-16 h-36 w-36 rounded-full bg-[#f5b25f]/30 blur-3xl" />

            <div className="relative flex h-full flex-col">
              <span className="inline-flex w-fit rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.25em] text-[#f7f1e7]">
                {heroBadge}
              </span>

              <h1 className="mt-6 max-w-xl font-serif text-4xl font-semibold leading-[1.02] sm:text-5xl">
                {heroTitle}
              </h1>
              <p className="mt-4 max-w-xl text-base leading-7 text-[#d5e2e4] sm:text-lg">
                {heroDescription}
              </p>

              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                {heroHighlights.map(({ icon: Icon, title, description }) => (
                  <div
                    key={title}
                    className="rounded-[1.5rem] border border-white/12 bg-white/10 p-4 backdrop-blur-sm"
                  >
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/12 text-[#ffd6a8]">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="mt-4 text-lg font-semibold">{title}</h3>
                    <p className="mt-2 text-sm leading-6 text-[#d3e1e5]">
                      {description}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-8 grid gap-3 sm:grid-cols-3">
                {heroMetrics.map((metric) => (
                  <div
                    key={metric.label}
                    className="rounded-[1.4rem] border border-white/12 bg-black/10 px-4 py-4"
                  >
                    <p className="font-serif text-2xl font-semibold text-[#fff1d5]">
                      {metric.value}
                    </p>
                    <p className="mt-1 text-sm font-medium text-[#cfe0e3]">
                      {metric.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <Card className="animate-rise border-[#eadfce]/90 bg-[#fffaf4]/88 shadow-[0_32px_72px_rgba(48,38,25,0.12)] backdrop-blur-xl [animation-delay:120ms] [animation-fill-mode:backwards]">
            <CardContent className="flex h-full flex-col px-6 py-8 sm:px-8 sm:py-10">
              <div className="space-y-4">
                <span className="inline-flex w-fit rounded-full border border-[#e8ddcf] bg-[#fff4e6] px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-[#9a5d2e]">
                  {formBadge}
                </span>

                <div className="space-y-3">
                  <h2 className="font-serif text-4xl font-semibold leading-tight text-[#16323b] sm:text-[2.75rem]">
                    {formTitle}
                  </h2>
                  <p className="max-w-xl text-base leading-7 text-[#6d736d] sm:text-lg">
                    {formDescription}
                  </p>
                </div>
              </div>

              <div className="mt-8 flex-1">{children}</div>

              {footer ? (
                <div className="mt-8 border-t border-[#eadfce] pt-6">{footer}</div>
              ) : null}
            </CardContent>
          </Card>
        </section>
      </div>
    </JourneyCanvas>
  );
}
